import { z } from "zod/v4"
import { StampLaneRewardId } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { ItemKindEnum } from "./item/item-kind"

export const StampLaneRewardKind = z.enum(["item", "coin"]).describe(
  "報酬の種別。'item'＝アイテム付与、'coin'＝無償POコイン付与"
)
export type StampLaneRewardKind = z.infer<typeof StampLaneRewardKind>

export const StampLaneRewardItemKind = z.union([
  ItemKindEnum,
  z.literal("external")
]).describe(
  "アイテムの種類。'look'（ホロスーツ）、'space-background'（背景）、'space-bgm'（BGM）、'external'（外部アイテム）"
)
export type StampLaneRewardItemKind = z.infer<typeof StampLaneRewardItemKind>

export const StampLaneRewardItem = z.object({
  item_kind: StampLaneRewardItemKind,
  item_id: z.string().describe("付与対象アイテムのID"),
  item_name_snapshot: z.string().describe("付与時点のアイテム表示名スナップショット"),
})

export type StampLaneRewardItem = z.infer<typeof StampLaneRewardItem>

export const StampLaneRewardCoin = z.object({
  amount: z.number().describe("付与する無償POコイン枚数（コイン）"),
  expires_at: z.number().nullable().describe("付与コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
})

export type StampLaneRewardCoin = z.infer<typeof StampLaneRewardCoin>

const StampLaneRewardBase = z.object({
  card_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  lane_id: z.string().describe("親ドキュメントIDの冗長保持（コレクショングループクエリ対応のため。パスのIDはクエリ条件に使用できない制約がある）"),
  name: z.string().describe("報酬名（エンドユーザー表示用）"),
  threshold_stamps: z.number().describe("受取に必要な累計スタンプ数。この数に到達すると受け取り可能になります"),
  featured: z.boolean().describe("おすすめ表示の対象かどうか"),
})

const StampLaneRewardItemMeta = StampLaneRewardBase.extend({
  kind: z.literal("item").describe("報酬の種別。'item'＝アイテム付与"),
  item: StampLaneRewardItem.describe("アイテム報酬の詳細"),
})

const StampLaneRewardCoinMeta = StampLaneRewardBase.extend({
  kind: z.literal("coin").describe("報酬の種別。'coin'＝無償POコイン付与"),
  coin: StampLaneRewardCoin.describe("コイン報酬の詳細"),
})

export const StampLaneRewardDocumentMeta = z.discriminatedUnion("kind", [
  StampLaneRewardItemMeta,
  StampLaneRewardCoinMeta,
])

export type StampLaneRewardDocumentMeta = z.infer<typeof StampLaneRewardDocumentMeta>

export const StampLaneRewardDocumentBody = z.intersection(
  StampLaneRewardDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StampLaneRewardDocumentBody = z.infer<typeof StampLaneRewardDocumentBody>

export type StampLaneRewardDocument = StampLaneRewardDocumentBody & {
  id: StampLaneRewardId,
}

const StampLaneRewardItemFieldsRequest = z.object({
  itemId: z.string().describe("付与対象アイテムのID"),
})

const MAX_TIMESTAMP_MILLIS = 8.64e15

const ExpiresAtInput = z.int().refine(n => 0 <= n && n <= MAX_TIMESTAMP_MILLIS, 'Invalid timestamp range').nullable()

const StampLaneRewardCoinFieldsRequest = z.object({
  amount: z.int().describe("付与する無償POコイン枚数（コイン）"),
  expiresAt: ExpiresAtInput.describe("付与コインの有効期限（UNIXタイムスタンプミリ秒、null可）"),
})

const StampLaneRewardCoinFieldsUpdateRequest = z.object({
  amount: z.int().optional(),
  expiresAt: ExpiresAtInput.optional(),
})

const StampLaneRewardBaseFieldsRequest = z.object({
  name: z.string().max(100).describe("報酬名（エンドユーザー表示用）"),
  thresholdStamps: z.number().describe("受取に必要な累計スタンプ数"),
  featured: z.boolean().describe("おすすめ表示の対象かどうか"),
})

const CreateStampLaneRewardItemRequest = StampLaneRewardBaseFieldsRequest.extend({
  kind: z.literal("item"),
  item: StampLaneRewardItemFieldsRequest,
})

const CreateStampLaneRewardCoinRequest = StampLaneRewardBaseFieldsRequest.extend({
  kind: z.literal("coin"),
  coin: StampLaneRewardCoinFieldsRequest,
})

export const CreateStampLaneRewardRequest = z.discriminatedUnion("kind", [
  CreateStampLaneRewardItemRequest,
  CreateStampLaneRewardCoinRequest,
])

export type CreateStampLaneRewardRequest = z.infer<typeof CreateStampLaneRewardRequest>

export const UpdateStampLaneRewardRequest = StampLaneRewardBaseFieldsRequest.partial().extend({
  kind: z.enum(["item", "coin"]).optional(),
  item: StampLaneRewardItemFieldsRequest.optional(),
  coin: StampLaneRewardCoinFieldsUpdateRequest.optional(),
})

export type UpdateStampLaneRewardRequest = z.infer<typeof UpdateStampLaneRewardRequest>

export class StampLaneRewardDocuments {

  static toDocumentData(doc: StampLaneRewardDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StampLaneRewardDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const kind = data["kind"]
    const base = {
      id: StampLaneRewardId(doc.id),
      card_id: data["card_id"],
      lane_id: data["lane_id"],
      name: data["name"] as string,
      threshold_stamps: data["threshold_stamps"],
      featured: data["featured"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    if (kind === "item") {
      return {
        ...base,
        kind: "item",
        item: data["item"],
      }
    } else {
      return {
        ...base,
        kind: "coin",
        coin: data["coin"],
      }
    }
  }
}
